import { useRef, useState } from 'react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import ErrorBoundary from '../components/ErrorBoundary';
import usePageMeta, { breadcrumbGraph } from '../hooks/usePageMeta';
import { useReveal, PageHero, SectionHead, PageCta } from './pageShared';
import './Contact.css';

const EMAIL = 'printablegraphpaper@gmail.com';
const CONTACT_URL = 'https://printablegraphpaperpdf.com/contact';

const CONTACT_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      name: 'Contact Printable Graph Paper',
      url: CONTACT_URL,
      inLanguage: 'en',
      description:
        'Questions, feedback, bug reports, or feature ideas for Printable Graph Paper — send a message or email the team directly.',
      isPartOf: { '@id': 'https://printablegraphpaperpdf.com/' },
    },
    breadcrumbGraph('Contact', CONTACT_URL),
  ],
};

const SUBJECTS = [
  'General question',
  'Feedback',
  'Bug report',
  'Feature suggestion',
  'Something else',
];

const REASONS = [
  {
    title: 'Questions',
    desc: 'How the generator works, paper sizes, margins, or printing tips.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M9.6 9.4a2.5 2.5 0 1 1 3.6 2.3c-.7.4-1.2 1-1.2 1.8v.3" />
        <path d="M12 17h.01" />
      </svg>
    ),
  },
  {
    title: 'Feedback',
    desc: 'Tell us what worked, what felt clunky, and what you would change.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 11.5a8.4 8.4 0 0 1-8.5 8.4 8.9 8.9 0 0 1-3.9-.9L3 20.5l1.6-4.6A8.4 8.4 0 0 1 12.5 3.1 8.4 8.4 0 0 1 21 11.5z" />
      </svg>
    ),
  },
  {
    title: 'Bug reports',
    desc: 'Something renders wrong or breaks? Include your browser and steps to reproduce.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 6.5V5" />
        <path d="M9.5 4.5 12 6.5l2.5-2" />
        <rect x="7.5" y="6.5" width="9" height="12" rx="4.5" />
        <path d="M7.5 10H4M7.5 14H4M16.5 10H20M16.5 14H20" />
        <path d="M9.5 18 8 21M14.5 18 16 21" />
      </svg>
    ),
  },
  {
    title: 'Feature suggestions',
    desc: 'Missing a paper type or grid size? We keep a running list of requests.',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 18h6" />
        <path d="M10 21h4" />
        <path d="M12 3a6 6 0 0 0-3.5 10.9c.5.4.8 1 .9 1.6l.1.5h5l.1-.5c.1-.6.4-1.2.9-1.6A6 6 0 0 0 12 3z" />
      </svg>
    ),
  },
];

const MailIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3 7 9 6 9-6" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

const PencilIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

function validate(values) {
  const errors = {};
  if (!values.name.trim()) {
    errors.name = 'Please enter your name.';
  }
  if (!values.email.trim()) {
    errors.email = 'Please enter your email address.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }
  if (!values.subject) {
    errors.subject = 'Please choose a subject.';
  }
  if (!values.message.trim()) {
    errors.message = 'Please tell us how we can help.';
  } else if (values.message.trim().length < 10) {
    errors.message = 'Please add a little more detail (at least 10 characters).';
  }
  return errors;
}

function buildMailto(values) {
  const subjectLine =
    '[Printable Graph Paper] ' +
    (values.subject || 'General question') +
    (values.name.trim() ? ' — ' + values.name.trim() : '');
  const bodyLines = [
    'Name: ' + values.name.trim(),
    'Email: ' + values.email.trim(),
    '',
    values.message.trim(),
    '',
    '— sent from printablegraphpaperpdf.com/contact',
  ];
  return (
    'mailto:' +
    EMAIL +
    '?subject=' +
    encodeURIComponent(subjectLine) +
    '&body=' +
    encodeURIComponent(bodyLines.join('\n'))
  );
}

function Contact() {
  usePageMeta({
    title: 'Contact Printable Graph Paper',
    description:
      'Questions, feedback, bug reports, or feature ideas for Printable Graph Paper — send a message or email the team directly.',
    keywords: 'contact, printable graph paper, feedback, bug report, feature request, support',
    canonical: 'https://printablegraphpaperpdf.com/contact',
    ogTitle: 'Contact Printable Graph Paper',
    ogDescription:
      'Questions, feedback, bug reports, or feature ideas — get in touch with the Printable Graph Paper team.',
    ogUrl: 'https://printablegraphpaperpdf.com/contact',
    jsonLd: CONTACT_JSONLD,
  });

  const revealRef = useReveal();
  const statusRef = useRef(null);
  const [values, setValues] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [errors, setErrors] = useState({});
  const [prepared, setPrepared] = useState(false);
  const [copyState, setCopyState] = useState('idle');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    const fields = ['name', 'email', 'subject', 'message'];
    const firstInvalid = fields.find((field) => nextErrors[field]);
    if (firstInvalid) {
      const el = document.getElementById('contact-' + firstInvalid);
      if (el) el.focus();
      return;
    }
    setCopyState('idle');
    setPrepared(true);
    requestAnimationFrame(() => {
      if (statusRef.current && typeof statusRef.current.scrollIntoView === 'function') {
        statusRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  };

  const handleCopy = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(EMAIL).then(
        () => setCopyState('copied'),
        () => setCopyState('failed')
      );
    } else {
      setCopyState('failed');
    }
  };

  const handleEdit = () => {
    setPrepared(false);
    const el = document.getElementById('contact-name');
    if (el) el.focus();
  };

  const visual = (
    <>
      <div className="contact-sheet">
        <span className="contact-sheet-label">Draft</span>
      </div>
      <div className="contact-card">
        <div className="contact-card-head">
          <span className="contact-card-avatar">
            <MailIcon size={15} />
          </span>
          <span className="contact-card-head-text">
            <span className="contact-card-title">New message</span>
            <span className="contact-card-sub">To · Printable Graph Paper</span>
          </span>
        </div>
        <div className="contact-card-lines">
          <span />
          <span />
          <span />
        </div>
        <div className="contact-card-foot">
          <span className="contact-card-input" />
          <span className="contact-card-send">Send</span>
        </div>
      </div>
      <div className="contact-float-chip">
        <MailIcon size={13} />
        {EMAIL}
      </div>
    </>
  );

  return (
    <ErrorBoundary>
      <div className="app page-root contact-page" ref={revealRef}>
        <SiteNav />
        <main className="page-main">
          <PageHero
            eyebrow="Get in touch"
            title="Have a question or an idea?"
            leads={[
              'Have a question, spotted a bug, or have an idea for a new paper type? Write to us — a real person reads every message.',
              'No account, no ticket queue: your note goes straight to our inbox.',
            ]}
            visual={visual}
          />

          <section className="contact-body">
            <div className="lp-container contact-grid">
              <div className="contact-info" data-reveal="true">
                <SectionHead
                  eyebrow="Reach out"
                  title="We read everything."
                  body="A short message with a few details — what you tried, what you expected — helps us reply faster."
                  reveal={false}
                />

                <ul className="contact-reasons">
                  {REASONS.map((reason) => (
                    <li className="contact-reason" key={reason.title}>
                      <span className="page-icon">{reason.icon}</span>
                      <span className="contact-reason-text">
                        <span className="contact-reason-title">{reason.title}</span>
                        <span className="contact-reason-desc">{reason.desc}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="contact-direct">
                  <p className="contact-direct-label">Email us directly</p>
                  <a className="contact-direct-link" href={'mailto:' + EMAIL}>
                    <MailIcon size={16} />
                    {EMAIL}
                  </a>
                  <p className="contact-direct-note">
                    Opens a new message in the email app you already use — nothing you type on this
                    page is sent anywhere until you press send yourself.
                  </p>
                </div>
              </div>

              <div className="contact-form-wrap" data-reveal="true">
                <form className="contact-form" id="contact-form" onSubmit={handleSubmit} noValidate>
                  <div className="contact-form-head">
                    <h3 className="contact-form-title">Send us a message</h3>
                    <p className="contact-form-sub">All fields are required.</p>
                  </div>

                  <div className="contact-row">
                    <div className="contact-field">
                      <label className="contact-label" htmlFor="contact-name">
                        Name<span className="contact-req">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        className="contact-input"
                        placeholder="Ada Lovelace"
                        autoComplete="name"
                        value={values.name}
                        onChange={handleChange}
                        aria-invalid={errors.name ? 'true' : undefined}
                        aria-describedby={errors.name ? 'contact-name-error' : undefined}
                      />
                      {errors.name && (
                        <span className="contact-error" id="contact-name-error">
                          {errors.name}
                        </span>
                      )}
                    </div>

                    <div className="contact-field">
                      <label className="contact-label" htmlFor="contact-email">
                        Email<span className="contact-req">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        className="contact-input"
                        placeholder="you@example.com"
                        autoComplete="email"
                        value={values.email}
                        onChange={handleChange}
                        aria-invalid={errors.email ? 'true' : undefined}
                        aria-describedby={errors.email ? 'contact-email-error' : undefined}
                      />
                      {errors.email && (
                        <span className="contact-error" id="contact-email-error">
                          {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="contact-field">
                    <label className="contact-label" htmlFor="contact-subject">
                      Subject<span className="contact-req">*</span>
                    </label>
                    <select
                      id="contact-subject"
                      name="subject"
                      className="contact-select"
                      value={values.subject}
                      onChange={handleChange}
                      aria-invalid={errors.subject ? 'true' : undefined}
                      aria-describedby={errors.subject ? 'contact-subject-error' : undefined}
                    >
                      <option value="">Choose a topic…</option>
                      {SUBJECTS.map((subject) => (
                        <option value={subject} key={subject}>
                          {subject}
                        </option>
                      ))}
                    </select>
                    {errors.subject && (
                      <span className="contact-error" id="contact-subject-error">
                        {errors.subject}
                      </span>
                    )}
                  </div>

                  <div className="contact-field">
                    <label className="contact-label" htmlFor="contact-message">
                      Message<span className="contact-req">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      className="contact-textarea"
                      placeholder="What can we help with? If it's a bug, which browser are you using?"
                      rows={5}
                      value={values.message}
                      onChange={handleChange}
                      aria-invalid={errors.message ? 'true' : undefined}
                      aria-describedby={errors.message ? 'contact-message-error' : undefined}
                    />
                    {errors.message && (
                      <span className="contact-error" id="contact-message-error">
                        {errors.message}
                      </span>
                    )}
                  </div>

                  <div className="contact-form-foot">
                    <p className="contact-form-note">
                      This site has no form server — step two opens your own email app, so nothing
                      is stored here.
                    </p>
                    <button type="submit" className="lp-btn-primary">
                      Send Message
                    </button>
                  </div>
                </form>

                {prepared && (
                  <div
                    className="contact-status"
                    ref={statusRef}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="contact-status-head">
                      <span className="contact-status-icon">
                        <CheckIcon />
                      </span>
                      <div className="contact-status-body">
                        <p className="contact-status-title">Your message is ready</p>
                        <p className="contact-status-text">
                          Nothing was uploaded — open your email app to send it to{' '}
                          <strong>{EMAIL}</strong>, or copy the address and write us later.
                        </p>
                      </div>
                    </div>
                    <div className="contact-status-actions">
                      <a className="lp-btn-primary" href={buildMailto(values)}>
                        Open email app
                      </a>
                      <button type="button" className="lp-btn-secondary" onClick={handleCopy}>
                        {copyState === 'copied' ? 'Copied' : 'Copy address'}
                      </button>
                      <button type="button" className="contact-status-edit" onClick={handleEdit}>
                        <PencilIcon />
                        Edit message
                      </button>
                    </div>
                    {copyState === 'failed' && (
                      <p className="contact-status-failed" role="alert">
                        Couldn't access your clipboard — select the address above and copy it
                        manually.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>

          <PageCta
            eyebrow="Looking for paper?"
            title="Just need graph paper?"
            body="Every sheet is a click away — pick a type, tweak the grid, and print."
            primary={{ label: 'Create graph paper', href: '/' }}
            secondary={{ label: 'Read the privacy policy', href: '/privacy' }}
            className="lp-section-tint"
          />
        </main>
        <SiteFooter />
      </div>
    </ErrorBoundary>
  );
}

export default Contact;
